import { getAuthHeaders, getBaseUrl } from './apiClient';

export type BlogCandidate = {
  candidate_id: string;
  candidate_name: string;
  content_pillar?: string;   // the loose "Category" bucket
  cluster?: string;          // optional tight hub-and-spoke group (distinct from content_pillar)
  pillar?: boolean;          // is this the pillar (hub) article of its cluster?
  icp: string;
  funnel_stage: 'Top' | 'Medium' | 'Bottom' | 'Authority';
  focus: 'B2B' | 'B2C';
  business_context?: string;
  directional_guidance?: string;
  /** Resolved from wordpress_category_slug at create/update; used by later pipeline stages. */
  wordpress_category?: {
    name: string;
    slug: string;
    wp_id: number;
  };
  status: 'draft' | 'active' | 'archived';
  /** When set, the article is treated as build-complete (excluded from pipeline and analytics lists). */
  published_confirmed_at?: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  version?: number;
};

export const blogCandidatesService = {
  async list(status?: 'draft' | 'active' | 'archived'): Promise<{ candidates: BlogCandidate[]; count: number }> {
    const qs = status ? `?status=${encodeURIComponent(status)}` : '';
    const resp = await fetch(`${getBaseUrl()}/secure/blog/candidates${qs}`, { headers: await getAuthHeaders() });
    if (!resp.ok) throw new Error(`Failed to list candidates (${resp.status})`);
    return await resp.json();
  },
  async get(candidateId: string): Promise<BlogCandidate> {
    const resp = await fetch(`${getBaseUrl()}/secure/blog/candidates/${encodeURIComponent(candidateId)}`, { headers: await getAuthHeaders() });
    if (!resp.ok) throw new Error(`Failed to get candidate (${resp.status})`);
    return await resp.json();
  },
  async create(payload: {
    candidate_name: string;
    icp: string;
    funnel_stage: BlogCandidate['funnel_stage'];
    focus: BlogCandidate['focus'];
    wordpress_category_slug: string;
    content_pillar?: string;
    cluster?: string;
    business_context?: string;
    directional_guidance?: string;
    status?: BlogCandidate['status'];
  }): Promise<BlogCandidate> {
    const resp = await fetch(`${getBaseUrl()}/secure/blog/candidates`, {
      method: 'POST',
      headers: await getAuthHeaders(),
      body: JSON.stringify(payload)
    });
    if (!resp.ok) {
      const text = await resp.text();
      throw new Error(`Failed to create candidate: ${resp.status} ${text}`);
    }
    return await resp.json();
  },
  async update(
    candidateId: string,
    payload: Partial<BlogCandidate> & { wordpress_category_slug?: string }
  ): Promise<BlogCandidate> {
    const resp = await fetch(`${getBaseUrl()}/secure/blog/candidates/${encodeURIComponent(candidateId)}`, {
      method: 'PUT',
      headers: await getAuthHeaders(),
      body: JSON.stringify(payload)
    });
    if (!resp.ok) {
      const text = await resp.text();
      throw new Error(`Failed to update candidate: ${resp.status} ${text}`);
    }
    return await resp.json();
  },
  async archive(candidateId: string): Promise<BlogCandidate> {
    const resp = await fetch(`${getBaseUrl()}/secure/blog/candidates/${encodeURIComponent(candidateId)}`, {
      method: 'DELETE',
      headers: await getAuthHeaders()
    });
    if (!resp.ok) {
      const text = await resp.text();
      throw new Error(`Failed to archive candidate: ${resp.status} ${text}`);
    }
    return await resp.json();
  }
};
